import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

// Redux

// Services
import { getTranslateByKey } from '@/main/services/utils';
import { VariableTypeEnum } from '@/main/services/enum/VariableTypeEnum';

// Types
import { KeyValue } from '@/main/models/common';
import { DynamicReportVariableForm } from '../../../registerDynamicReport';

// Api
import { variableTypeApi } from '@/main/api';


// Components
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogCheckBoxControlled from '@/main/components/shared/checkbox/checkBoxControlled';

import * as S from './styles';

interface INewVariableProps {
  variableToEdit: DynamicReportVariableForm;
  usedVariableNames: string[],
  usedFilterNames: string[],
  addNew: (newVariable: DynamicReportVariableForm) => void;
  finishEdit: (editedVariable: DynamicReportVariableForm | null) => void;
}

function NewVariable({
  variableToEdit,
  usedVariableNames,
  usedFilterNames,
  addNew,
  finishEdit,
}: INewVariableProps) {

  const [variableTypes, setVariableTypes] = useState<KeyValue[]>([]);
  const [variableTypesLoading, setVariableTypesLoading] = useState<boolean>(false);
  const [isDateRange, setIsDateRange] = useState<boolean>(false);

  const isEdit = !!variableToEdit;
  const INITIAL_FORM_STATE : DynamicReportVariableForm =  {
    guidDynamicReportVariable: null,
    variableType:  null,
    variableName: '',
    variableRangeName: '',
    filterName: '',
    mandatory: false,
    ordering: 0,
    maxAllowedRange: null,
  }
  const methods = useForm<DynamicReportVariableForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
  });

  const {
    getValues,
    setValue,
    reset,
    trigger,
    formState: {isValid},
    control
  } = methods;


  const watcherVariableType = useWatch({ control, name: `variableType` });



  useEffect( () => {
    setVariableTypesLoading(true);
    variableTypeApi.getDomain()
    .then( (result) => {
        setVariableTypes(result.data.result);
      })
      .finally(() => {setVariableTypesLoading(false);});
  }, []);

  useEffect( () => {
    if (!!watcherVariableType){
      if (watcherVariableType.key == VariableTypeEnum.DateRange && !isDateRange)
        setIsDateRange(true);
      else if (watcherVariableType.key != VariableTypeEnum.DateRange && isDateRange){
        setValue('variableRangeName', '');
        setValue('maxAllowedRange', null);
        setIsDateRange(false);
      }
    }
  }, [watcherVariableType]);

  useEffect( () => {
    if (isEdit) {
      const value = variableToEdit;
      setValue('variableType', value.variableType);
      setValue('variableName', value.variableName);
      setValue('variableRangeName', value.variableRangeName);
      setValue('filterName', value.filterName);
      setValue('maxAllowedRange', value.maxAllowedRange);
      setValue('mandatory', value.mandatory);
      setValue('ordering', value.ordering);
      setValue('guidDynamicReportVariable', value.guidDynamicReportVariable);
    }
  }, [variableToEdit]);




  function generatedValidatorVariableName(fieldName: string) {
    return (name: string) => {
      if (!/^[a-zA-Z]\w*$/.test(name))
        return getTranslateByKey('information.fieldInvalid', true).replace('{0}', fieldName);
      if (usedVariableNames.find( used => used == name))
        return getTranslateByKey('information.fieldRepeated', true).replace('{0}', fieldName);
      return true;
    }
  }

  function generatedValidatorFilterName(fieldName: string) {
    return (name: string) => {
      if (usedFilterNames.find( used => used == name))
        return getTranslateByKey('information.fieldRepeated', true).replace('{0}', fieldName);
      return true;
    }
  }



  function SaveToList() {
    if (!isValid)
      return trigger();
    const data = getValues();
    if (!isEdit) {
      addNew(data);
    }
    else {
      finishEdit(data);
    }
    reset(INITIAL_FORM_STATE);
    setIsDateRange(false);
  }

  function cancelEdit(){
    reset(INITIAL_FORM_STATE);
    setIsDateRange(false);
    finishEdit(null);
  }

  return (
    <>
      <S.NewVariableArea>
        <KogInputTextControlledNew
          label={getTranslateByKey('dynamicReportVariable.variableName')}
          name='variableName'
          control={control}
          controllerProps={{ rules:{ required: true, validate: generatedValidatorVariableName(getTranslateByKey('dynamicReportVariable.variableName')) }}}
          inputProps={{ maxLength: 50 }}
          id='id-newVariable-variableName'
        />
        {
          isDateRange &&
          (
            <KogInputTextControlledNew
              label={getTranslateByKey('dynamicReportVariable.variableRangeName')}
              name='variableRangeName'
              control={control}
              controllerProps={{ rules:{ required: true, validate: generatedValidatorVariableName(getTranslateByKey('dynamicReportVariable.variableRangeName')) }}}
              inputProps={{ maxLength: 50 }}
              id='id-newVariable-variableRangeName'
            />
          )
        }
        <KogAutoCompleteControlled
          label={getTranslateByKey('dynamicReportVariable.variableType')}
          nameCustom='variableType'
          control={control}
          rulesCustom={ { required: true } }
          onChangeCustom={() => {}}
          options={variableTypes}
          isLoading={variableTypesLoading}
          id='id-newVariable-variableType'
        />
        <KogInputTextControlledNew
          label={getTranslateByKey('dynamicReportVariable.filterName')}
          name='filterName'
          control={control}
          controllerProps={{ rules:{ required: true, validate: generatedValidatorFilterName(getTranslateByKey('dynamicReportVariable.filterName')) }}}
          inputProps={{ maxLength: 50 }}
          id='id-newVariable-variableName'
        />
        {
          isDateRange &&
          <KogInputTextControlledNew
            label={getTranslateByKey('dynamicReportVariable.maxAllowedRange')}
            name='maxAllowedRange'
            hasOnlyNumberInteger
            control={control}
            controllerProps={{ rules:{ required: true }}}
            inputProps={{ maxLength: 3 }}
            id='id-newVariable-maxAllowedRange'
          />
        }
        <S.CheckBoxManadatory>
          <KogCheckBoxControlled
            nameCustom='mandatory'
            control={control}
            label={getTranslateByKey('dynamicReportVariable.mandatory')}
            tooltipMessage={getTranslateByKey('dynamicReportVariable.mandatoryCheckboxTooltip')}
            id='id-newVariable-mandatory'
          />
        </S.CheckBoxManadatory>
      </S.NewVariableArea>
      <S.AreaButton>
        <SecondaryButton onClick={ SaveToList } >
          { isEdit ? getTranslateByKey('information.edit')  : getTranslateByKey('dynamicReportVariable.addFilter') }
        </SecondaryButton>
        {
          isEdit &&
          <SecondaryButton onClick={ cancelEdit } >
            { getTranslateByKey('information.cancel') }
          </SecondaryButton>
        }
      </S.AreaButton>
    </>
  );
}


export default NewVariable;